import styles from './Screenshots.module.scss';
import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import moment from 'moment-timezone';
import {
  getScreenshotFullSrc,
  getScreenshotPreviewSrc,
} from '@/utils/screenshotPrivacy';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Button } from 'reactstrap';

export const ScreenshotsModal = ({
  allScreenshotsList,
  selectedScreenshotIndex,
  selectedTimezone,
  setSelectedScreenshotIndex,
}) => {
  const selectNext = () => {
    if (allScreenshotsList.length === 0) return;
    setSelectedScreenshotIndex((prev) => {
      const next = prev === allScreenshotsList.length - 1 ? 0 : prev + 1;
      return next;
    });
  };

  const selectPrev = () => {
    if (allScreenshotsList.length === 0) return;
    setSelectedScreenshotIndex((prev) => {
      const next = prev === 0 ? allScreenshotsList.length - 1 : prev - 1;
      return next;
    });
  };

  const currentShot = allScreenshotsList[selectedScreenshotIndex];
  const mainSrc = getScreenshotFullSrc(currentShot);

  return (
    <div className={styles.screenshotsModal}>
      <Text variant={textVariantsEnum.Text_13} fontWeight={fontWeightEnum.Bold}>
        Screenshot{' '}
        {allScreenshotsList[selectedScreenshotIndex]?.dayLabel
          ? `- ${allScreenshotsList[selectedScreenshotIndex].dayLabel}`
          : ''}
      </Text>
      <div className={styles.screenshotsModalContent}>
        {allScreenshotsList.length === 0 ? (
          <div className={styles.noScreenshots}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Bold}
            >
              No screenshots available.
            </Text>
          </div>
        ) : (
          <>
            <div
              className="d-flex align-items-center justify-content-center mb-3 position-relative"
              style={{
                minHeight: '280px',
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '12px',
              }}
            >
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={selectPrev}
                style={{
                  position: 'absolute',
                  left: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '44px',
                  height: '44px',
                  padding: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  border: 'none',
                  zIndex: 10,
                }}
                aria-label="Previous screenshot"
              >
                <FiChevronLeft size={22} />
              </Button>
              <div className="text-center w-100">
                <img
                  src={mainSrc}
                  alt="Screenshot preview"
                  style={{
                    maxHeight: '60vh',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    borderRadius: '6px',
                  }}
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='12'%3EImage not available%3C/text%3E%3C/svg%3E";
                    e.target.onerror = null;
                  }}
                />
                <div className="mt-2">
                  <div className="font-weight-bold">
                    {allScreenshotsList[selectedScreenshotIndex]?.timestamp
                      ? moment(
                          allScreenshotsList[selectedScreenshotIndex].timestamp
                        )
                          .tz(selectedTimezone)
                          .format('DD MMM YYYY, hh:mm A')
                      : 'Timestamp unavailable'}
                  </div>
                  <small className="text-muted">
                    {`Screenshot ${selectedScreenshotIndex + 1} of ${
                      allScreenshotsList.length
                    }`}
                  </small>
                  <div>
                    <a
                      href={getScreenshotFullSrc(
                        allScreenshotsList[selectedScreenshotIndex]
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in new tab
                    </a>
                  </div>
                </div>
              </div>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={selectNext}
                style={{
                  position: 'absolute',
                  right: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '44px',
                  height: '44px',
                  padding: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  border: 'none',
                  zIndex: 10,
                }}
                aria-label="Next screenshot"
              >
                <FiChevronRight size={22} />
              </Button>
            </div>
            <div>
              <div className="mb-2 font-weight-bold">All screenshots</div>
              <div
                className={'with-scroll'}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '12px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                {allScreenshotsList.map((item, idx) => (
                  <div
                    key={`${item.activityId || 'activity'}-${idx}`}
                    className="text-center"
                    style={{
                      border:
                        idx === selectedScreenshotIndex
                          ? '2px solid #007bff'
                          : '1px solid #e9ecef',
                      borderRadius: '6px',
                      padding: '6px',
                      cursor: 'pointer',
                      background:
                        idx === selectedScreenshotIndex ? '#e9f2ff' : '#fff',
                    }}
                    onClick={() => setSelectedScreenshotIndex(idx)}
                  >
                    <img
                      src={getScreenshotPreviewSrc(item)}
                      alt="Screenshot thumb"
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='12'%3EImage not available%3C/text%3E%3C/svg%3E";
                        e.target.onerror = null;
                      }}
                    />
                    <small className="d-block mt-1 text-truncate">
                      {item.timestamp
                        ? moment(item.timestamp)
                            .tz(selectedTimezone)
                            .format('hh:mm A')
                        : 'No time'}
                    </small>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
